import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  createProgrammeTarget,
  deleteProgrammeTarget,
  listProgrammeTargets,
  ManagementOptions,
  ProgrammeTarget,
  ProgrammeTargetInput,
  updateProgrammeTarget,
} from '../../api/managementReports';

const errorText = (err: unknown) =>
  isAxiosError(err) ? ((err.response?.data as { message?: string } | undefined)?.message ?? 'The target could not be saved.') : 'The target could not be saved.';

const EMPTY = { universityId: '', courseId: '', intakeId: '', target: '', notes: '' };

/** Programme targets (KPI workbook column D): a number of students per
 * university — or one of its programmes — per intake. Read-only without
 * management_reports:manage. */
export const ProgrammeTargetsDialog = ({
  open,
  onClose,
  options,
  canManage,
}: {
  open: boolean;
  onClose: () => void;
  options: ManagementOptions | undefined;
  canManage: boolean;
}) => {
  const queryClient = useQueryClient();
  const targets = useQuery({ queryKey: ['management-reports', 'programme-targets'], queryFn: listProgrammeTargets, enabled: open });
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ProgrammeTarget | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['management-reports'] });
  const reset = () => {
    setForm(EMPTY);
    setEditing(null);
    setError(null);
  };
  const save = useMutation({
    mutationFn: (input: ProgrammeTargetInput) => (editing ? updateProgrammeTarget(editing, input) : createProgrammeTarget(input)),
    onSuccess: () => {
      reset();
      void refresh();
    },
    onError: (err) => setError(errorText(err)),
  });
  const remove = useMutation({
    mutationFn: (id: number) => deleteProgrammeTarget(id),
    onSuccess: () => {
      setConfirmDelete(null);
      void refresh();
    },
    onError: (err) => setError(errorText(err)),
  });

  const courses = options?.courses.filter((c) => String(c.universityId) === form.universityId) ?? [];
  const targetNumber = Number(form.target);
  const valid = !!form.universityId && !!form.intakeId && form.target !== '' && Number.isInteger(targetNumber) && targetNumber >= 0;
  const submit = () =>
    save.mutate({
      universityId: Number(form.universityId),
      courseId: form.courseId ? Number(form.courseId) : null,
      intakeId: Number(form.intakeId),
      target: targetNumber,
      notes: form.notes.trim() || null,
    });
  const edit = (t: ProgrammeTarget) => {
    setEditing(t.id);
    setError(null);
    setForm({ universityId: String(t.universityId), courseId: t.courseId ? String(t.courseId) : '', intakeId: String(t.intakeId), target: String(t.target), notes: t.notes ?? '' });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Programme targets</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How many students IMC aims to recruit per university — or per programme of it — for each intake. A student counts towards their programme’s target, or
          their university’s university-wide target, for their intake, whenever they registered. A university can have either a university-wide target or
          programme targets for an intake, not both.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {canManage && (
          <Box sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {editing ? 'Edit target' : 'Add a target'}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
              <TextField
                select
                size="small"
                label="University"
                value={form.universityId}
                onChange={(e) => setForm({ ...form, universityId: e.target.value, courseId: '' })}
                sx={{ minWidth: 200 }}
              >
                {(options?.universities ?? []).map((u) => (
                  <MenuItem key={u.id} value={String(u.id)}>
                    {u.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Programme"
                value={form.courseId}
                onChange={(e) => setForm({ ...form, courseId: e.target.value })}
                sx={{ minWidth: 200 }}
                disabled={!form.universityId}
              >
                <MenuItem value="">
                  <em>All programmes (university-wide)</em>
                </MenuItem>
                {courses.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Intake" value={form.intakeId} onChange={(e) => setForm({ ...form, intakeId: e.target.value })} sx={{ minWidth: 170 }}>
                {(options?.intakes ?? []).map((i) => (
                  <MenuItem key={i.id} value={String(i.id)}>
                    {i.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="number"
                label="Target (students)"
                value={form.target}
                onChange={(e) => setForm({ ...form, target: e.target.value })}
                slotProps={{ htmlInput: { min: 0, step: 1 } }}
                sx={{ width: 150 }}
              />
              <TextField size="small" label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} sx={{ flex: 1, minWidth: 160 }} />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button variant="contained" size="small" disabled={!valid || save.isPending} onClick={submit}>
                {editing ? 'Save target' : 'Add target'}
              </Button>
              {editing && (
                <Button size="small" onClick={reset}>
                  Cancel
                </Button>
              )}
            </Stack>
          </Box>
        )}
        {targets.isError && <Alert severity="error">The targets could not be loaded.</Alert>}
        <Table size="small" aria-label="Programme targets">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Intake</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Country</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>University</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Programme</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">
                Target
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
              {canManage && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {targets.data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    No targets yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {targets.data?.map((t) => (
              <TableRow key={t.id} selected={editing === t.id}>
                <TableCell>{t.intakeName}</TableCell>
                <TableCell>{t.countryName ?? '—'}</TableCell>
                <TableCell>{t.universityName}</TableCell>
                <TableCell>{t.courseName ?? 'All programmes'}</TableCell>
                <TableCell align="right">{t.target}</TableCell>
                <TableCell>{t.notes}</TableCell>
                {canManage && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => edit(t)} aria-label={`Edit target ${t.universityName} ${t.intakeName}`}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => setConfirmDelete(t)} aria-label={`Remove target ${t.universityName} ${t.intakeName}`}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Remove this target?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmDelete && `${confirmDelete.universityName} · ${confirmDelete.courseName ?? 'All programmes'} · ${confirmDelete.intakeName}: ${confirmDelete.target} students.`}{' '}
            Students are not affected; the reports will show no target for this line. The removal is recorded in the audit log.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={remove.isPending} onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
